import React, { useState, useEffect, useRef } from 'react';
import { Trophy, Star, Coins } from 'lucide-react';

const SlotMachineAnimation = ({ 
  isVisible, 
  onClose, 
  type = 'points', // 'points' ou 'reward'
  oldValue = 0, 
  newValue = 0, 
  rewardName = '',
  duration = 3000 
}) => {
  const [currentValue, setCurrentValue] = useState(oldValue);
  const [isSpinning, setIsSpinning] = useState(false);
  const [phase, setPhase] = useState('idle'); // 'idle', 'spinning', 'finished'
  const [displayNumbers, setDisplayNumbers] = useState(() => {
    const maxValue = Math.max(oldValue || 0, newValue || 0);
    const digits = Math.max(3, maxValue.toString().length); // Mínimo 3 dígitos
    const initialValue = oldValue || 0;
    return initialValue.toString().padStart(digits, '0').split('');
  });
  
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isVisible && phase === 'idle') {
      // Inicializar valores corretamente
      setCurrentValue(oldValue);
      const maxValue = Math.max(oldValue || 0, newValue || 0);
      const digits = Math.max(3, maxValue.toString().length);
      const initialNumbers = (oldValue || 0).toString().padStart(digits, '0').split('');
      setDisplayNumbers(initialNumbers);
      
      // Aguardar um frame antes de iniciar a animação
      setTimeout(() => {
        startAnimation();
      }, 50);
    }
    
    // Reset phase quando animação se torna invisível
    if (!isVisible && phase !== 'idle') {
      setPhase('idle');
      setIsSpinning(false);
      setCurrentValue(oldValue);
      const maxValue = Math.max(oldValue || 0, newValue || 0);
      const digits = Math.max(3, maxValue.toString().length);
      const resetNumbers = (oldValue || 0).toString().padStart(digits, '0').split('');
      setDisplayNumbers(resetNumbers);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      cleanupAudio();
    };
  }, [isVisible, phase, oldValue]);

  const startAnimation = () => {
    setPhase('spinning');
    setIsSpinning(true);
    
    // Tocar som de roleta
    playSpinSound();
    
    // Iniciar animação dos números
    startNumberRolling();
    
    // Após a duração, finalizar animação
    setTimeout(() => {
      finishAnimation();
    }, duration);
  };

  const startNumberRolling = () => {
    let speed = 80; // Velocidade inicial
    const slowDownAfter = duration * 0.7;
    const animationStartTime = Date.now();
    
    // Calcular quantos dígitos precisamos
    const maxValue = Math.max(oldValue || 0, newValue || 0);
    const totalDigits = Math.max(3, maxValue.toString().length);
    
    // Usar setInterval em vez de setTimeout recursivo
    const rollInterval = setInterval(() => {
      // Verificar se ainda devemos continuar
      if (!isSpinning || phase !== 'spinning') {
        clearInterval(rollInterval);
        return;
      }
      
      const realElapsedTime = Date.now() - animationStartTime;
      
      // Parar se excedeu a duração
      if (realElapsedTime >= duration) {
        clearInterval(rollInterval);
        return;
      }
      
      // Gerar números aleatórios para todos os dígitos
      const newNumbers = Array.from({ length: totalDigits }, () => 
        Math.floor(Math.random() * 10).toString()
      );
      
      setDisplayNumbers(newNumbers);
      
      // Animar valor atual
      const progress = Math.min(realElapsedTime / duration, 1);
      const randomOffset = Math.floor(Math.random() * Math.abs(newValue - oldValue) * 0.1);
      const currentDisplayValue = Math.floor(oldValue + (newValue - oldValue) * progress + randomOffset);
      setCurrentValue(Math.max(0, currentDisplayValue));
      
      // Ajustar velocidade do interval se necessário
      if (realElapsedTime > slowDownAfter) {
        speed = Math.min(speed * 1.02, 150);
      }
      
    }, speed);
    
    // Armazenar referência para limpeza
    intervalRef.current = rollInterval;
  };

  const playSpinSound = () => {
    try {
      // Criar som de roleta moderno mais realista
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const audioContext = audioContextRef.current;
      const durationSeconds = duration / 1000;
      
      // Som principal de engrenagem girando e acelerando
      const createSpinningMachineSound = () => {
        const bufferSize = audioContext.sampleRate * durationSeconds;
        const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
        const output = buffer.getChannelData(0);
        
        for (let i = 0; i < bufferSize; i++) {
          const progress = i / bufferSize;
          
          // Som de motor/engrenagem acelerando
          const baseFreq = 40 + progress * 120; // 40Hz para 160Hz (aceleração)
          const motorSound = Math.sin(i * baseFreq * 0.0002) * 0.15;
          
          // Ruído de atrito/rotação
          let frictionNoise = (Math.random() * 2 - 1) * 0.06;
          
          // Modulação rotacional que acelera
          const rotationSpeed = 1 + progress * 8; // Acelera 8x durante a animação
          const rotationMod = Math.sin(i * rotationSpeed * 0.003) * 0.4;
          frictionNoise *= (1 + rotationMod);
          
          // Envelope de aceleração e depois desaceleração
          let envelope;
          if (progress < 0.3) {
            // Aceleração inicial
            envelope = Math.pow(progress / 0.3, 0.5);
          } else if (progress < 0.7) {
            // Velocidade máxima
            envelope = 1.0;
          } else {
            // Desaceleração gradual
            envelope = Math.pow((1 - progress) / 0.3, 0.4);
          }
          
          // Harmonicos que simulam engrenagens
          const gear1 = Math.sin(i * baseFreq * 2 * 0.0002) * 0.03 * envelope;
          const gear2 = Math.sin(i * baseFreq * 3 * 0.0002) * 0.02 * envelope;
          const gear3 = Math.sin(i * baseFreq * 1.5 * 0.0002) * 0.025 * envelope;
          
          // Combinação final
          output[i] = (motorSound + frictionNoise + gear1 + gear2 + gear3) * envelope;
        }
        
        return buffer;
      };
      
      // Reproduzir som principal de máquina girando
      const bufferSource = audioContext.createBufferSource();
      const gainNode = audioContext.createGain();
      const lowPassFilter = audioContext.createBiquadFilter();
      const highPassFilter = audioContext.createBiquadFilter();
      const compressor = audioContext.createDynamicsCompressor();
      
      bufferSource.buffer = createSpinningMachineSound();
      bufferSource.connect(lowPassFilter);
      lowPassFilter.connect(highPassFilter);
      highPassFilter.connect(compressor);
      compressor.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Filtro passa-baixa para som mais orgânico
      lowPassFilter.type = 'lowpass';
      lowPassFilter.frequency.setValueAtTime(800, audioContext.currentTime);
      lowPassFilter.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + durationSeconds);
      lowPassFilter.Q.setValueAtTime(1, audioContext.currentTime);
      
      // Filtro passa-alta para clareza
      highPassFilter.type = 'highpass';
      highPassFilter.frequency.setValueAtTime(30, audioContext.currentTime);
      highPassFilter.Q.setValueAtTime(0.5, audioContext.currentTime);
      
      // Compressor para consistência
      compressor.threshold.setValueAtTime(-20, audioContext.currentTime);
      compressor.knee.setValueAtTime(5, audioContext.currentTime);
      compressor.ratio.setValueAtTime(4, audioContext.currentTime);
      compressor.attack.setValueAtTime(0.003, audioContext.currentTime);
      compressor.release.setValueAtTime(0.1, audioContext.currentTime);
      
      // Envelope de volume com aceleração
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.3); // Acelera
      gainNode.gain.setValueAtTime(0.12, audioContext.currentTime + durationSeconds * 0.4); // Pico
      gainNode.gain.exponentialRampToValueAtTime(0.06, audioContext.currentTime + durationSeconds * 0.8); // Mantém
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + durationSeconds); // Para
      
      bufferSource.start();
      bufferSource.stop(audioContext.currentTime + durationSeconds);
      
      // Som adicional de aceleração (efeito whoosh crescente)
      const createAccelerationWhoosh = () => {
        const whooshOsc = audioContext.createOscillator();
        const whooshGain = audioContext.createGain();
        const whooshFilter = audioContext.createBiquadFilter();
        
        whooshOsc.connect(whooshFilter);
        whooshFilter.connect(whooshGain);
        whooshGain.connect(audioContext.destination);
        
        whooshOsc.type = 'sawtooth';
        whooshOsc.frequency.setValueAtTime(60, audioContext.currentTime);
        whooshOsc.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + durationSeconds * 0.6);
        whooshOsc.frequency.exponentialRampToValueAtTime(80, audioContext.currentTime + durationSeconds);
        
        whooshFilter.type = 'bandpass';
        whooshFilter.frequency.setValueAtTime(150, audioContext.currentTime);
        whooshFilter.frequency.exponentialRampToValueAtTime(300, audioContext.currentTime + durationSeconds * 0.5);
        whooshFilter.frequency.exponentialRampToValueAtTime(100, audioContext.currentTime + durationSeconds);
        whooshFilter.Q.setValueAtTime(2, audioContext.currentTime);
        
        whooshGain.gain.setValueAtTime(0, audioContext.currentTime);
        whooshGain.gain.linearRampToValueAtTime(0.04, audioContext.currentTime + 0.2);
        whooshGain.gain.setValueAtTime(0.06, audioContext.currentTime + durationSeconds * 0.6);
        whooshGain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + durationSeconds);
        
        whooshOsc.start();
        whooshOsc.stop(audioContext.currentTime + durationSeconds);
      };
      
      createAccelerationWhoosh();
      
      // Cliques mecânicos que aceleram e depois desaceleram
      const addMechanicalClicks = () => {
        let clickTime = audioContext.currentTime + 0.4;
        let baseInterval = 0.2; // Começa lento
        const minInterval = 0.05; // Velocidade máxima
        const accelerationPhase = durationSeconds * 0.4; // 40% acelerando
        const steadyPhase = durationSeconds * 0.3; // 30% velocidade constante
        // 30% restante desacelerando
        
        while (clickTime < audioContext.currentTime + durationSeconds * 0.95) {
          const timeProgress = (clickTime - audioContext.currentTime) / durationSeconds;
          
          // Criar som de click mecânico
          const clickOsc1 = audioContext.createOscillator();
          const clickOsc2 = audioContext.createOscillator();
          const clickGain = audioContext.createGain();
          const clickFilter = audioContext.createBiquadFilter();
          
          clickOsc1.connect(clickFilter);
          clickOsc2.connect(clickFilter);
          clickFilter.connect(clickGain);
          clickGain.connect(audioContext.destination);
          
          // Sons duplos para mais realismo (como engrenagem)
          clickOsc1.type = 'square';
          clickOsc2.type = 'triangle';
          
          const baseFreq = 400 + Math.random() * 300;
          clickOsc1.frequency.setValueAtTime(baseFreq, clickTime);
          clickOsc1.frequency.exponentialRampToValueAtTime(baseFreq * 0.4, clickTime + 0.04);
          
          clickOsc2.frequency.setValueAtTime(baseFreq * 1.5, clickTime);
          clickOsc2.frequency.exponentialRampToValueAtTime(baseFreq * 0.6, clickTime + 0.03);
          
          clickFilter.type = 'bandpass';
          clickFilter.frequency.setValueAtTime(600 + Math.random() * 200, clickTime);
          clickFilter.Q.setValueAtTime(3, clickTime);
          
          // Volume baseado na velocidade (clicks mais altos quando mais rápido)
          let clickVolume;
          if (timeProgress < 0.4) {
            // Acelerando - volume aumenta
            clickVolume = 0.02 + (timeProgress / 0.4) * 0.04;
          } else if (timeProgress < 0.7) {
            // Velocidade máxima - volume alto
            clickVolume = 0.06;
          } else {
            // Desacelerando - volume diminui
            clickVolume = 0.06 * (1 - (timeProgress - 0.7) / 0.3);
          }
          
          clickGain.gain.setValueAtTime(0, clickTime);
          clickGain.gain.linearRampToValueAtTime(clickVolume, clickTime + 0.003);
          clickGain.gain.exponentialRampToValueAtTime(0.001, clickTime + 0.05);
          
          clickOsc1.start(clickTime);
          clickOsc1.stop(clickTime + 0.05);
          clickOsc2.start(clickTime);
          clickOsc2.stop(clickTime + 0.04);
          
          // Calcular próximo intervalo baseado na fase
          let currentInterval;
          if (timeProgress < 0.4) {
            // Fase de aceleração - intervalo diminui
            const accelProgress = timeProgress / 0.4;
            currentInterval = baseInterval - (baseInterval - minInterval) * Math.pow(accelProgress, 1.5);
          } else if (timeProgress < 0.7) {
            // Velocidade constante
            currentInterval = minInterval;
          } else {
            // Fase de desaceleração - intervalo aumenta
            const decelProgress = (timeProgress - 0.7) / 0.3;
            currentInterval = minInterval + (baseInterval - minInterval) * Math.pow(decelProgress, 0.7);
          }
          
          // Adicionar pequena variação aleatória para naturalidade
          currentInterval += (Math.random() - 0.5) * 0.01;
          
          clickTime += currentInterval;
        }
      };
      
      addMechanicalClicks();
      
      // Som adicional de "spin up" inicial (como motor ligando)
      setTimeout(() => {
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          const spinUpOsc = audioContextRef.current.createOscillator();
          const spinUpGain = audioContextRef.current.createGain();
          const spinUpFilter = audioContextRef.current.createBiquadFilter();
          
          spinUpOsc.connect(spinUpFilter);
          spinUpFilter.connect(spinUpGain);
          spinUpGain.connect(audioContextRef.current.destination);
          
          spinUpOsc.type = 'sawtooth';
          spinUpOsc.frequency.setValueAtTime(30, audioContextRef.current.currentTime);
          spinUpOsc.frequency.exponentialRampToValueAtTime(120, audioContextRef.current.currentTime + 0.8);
          
          spinUpFilter.type = 'lowpass';
          spinUpFilter.frequency.setValueAtTime(200, audioContextRef.current.currentTime);
          spinUpFilter.frequency.exponentialRampToValueAtTime(600, audioContextRef.current.currentTime + 0.8);
          spinUpFilter.Q.setValueAtTime(2, audioContextRef.current.currentTime);
          
          spinUpGain.gain.setValueAtTime(0, audioContextRef.current.currentTime);
          spinUpGain.gain.linearRampToValueAtTime(0.05, audioContextRef.current.currentTime + 0.1);
          spinUpGain.gain.setValueAtTime(0.05, audioContextRef.current.currentTime + 0.6);
          spinUpGain.gain.exponentialRampToValueAtTime(0.001, audioContextRef.current.currentTime + 0.8);
          
          spinUpOsc.start();
          spinUpOsc.stop(audioContextRef.current.currentTime + 0.8);
        }
      }, 20);
      
    } catch (error) {
      console.warn('Erro ao reproduzir som de spin:', error);
    }
  };

  const playWinSound = () => {
    try {
      // Reutilizar o mesmo AudioContext se ainda estiver ativo
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const audioContext = audioContextRef.current;
      
      // Som de vitória moderno com acordes e efeitos
      const createChordTone = (frequency, startTime, duration = 0.8, volume = 0.2) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        const filter = audioContext.createBiquadFilter();
        
        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, startTime);
        
        // Filtro para dar brilho
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(3000, startTime);
        filter.frequency.exponentialRampToValueAtTime(1000, startTime + duration);
        filter.Q.setValueAtTime(1, startTime);
        
        // Envelope suave
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(volume * 0.3, startTime + duration * 0.6);
        gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      // Acorde de vitória (Dó Maior)
      const baseTime = audioContext.currentTime;
      const chordFrequencies = [523.25, 659.25, 783.99]; // C5, E5, G5
      
      // Tocar acorde completo
      chordFrequencies.forEach((freq, index) => {
        createChordTone(freq, baseTime + index * 0.05, 1.2, 0.15);
      });
      
      // Adicionar oitava superior para brilho
      setTimeout(() => {
        createChordTone(1046.5, audioContext.currentTime, 0.8, 0.1); // C6
      }, 200);
      
      // Efeito de "cascata" descendente
      const cascadeFrequencies = [1568, 1397, 1244, 1109, 988, 880, 784];
      cascadeFrequencies.forEach((freq, index) => {
        setTimeout(() => {
          if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            const oscillator = audioContextRef.current.createOscillator();
            const gainNode = audioContextRef.current.createGain();
            const filter = audioContextRef.current.createBiquadFilter();
            
            oscillator.connect(filter);
            filter.connect(gainNode);
            gainNode.connect(audioContextRef.current.destination);
            
            oscillator.type = 'triangle';
            oscillator.frequency.setValueAtTime(freq, audioContextRef.current.currentTime);
            
            filter.type = 'bandpass';
            filter.frequency.setValueAtTime(freq * 2, audioContextRef.current.currentTime);
            filter.Q.setValueAtTime(3, audioContextRef.current.currentTime);
            
            gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.08, audioContextRef.current.currentTime + 0.01);
            gainNode.gain.exponentialRampToValueAtTime(0.001, audioContextRef.current.currentTime + 0.15);
            
            oscillator.start();
            oscillator.stop(audioContextRef.current.currentTime + 0.15);
          }
        }, 600 + index * 60);
      });
      
      // Efeito de "shimmer" final
      setTimeout(() => {
        for (let i = 0; i < 5; i++) {
          setTimeout(() => {
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
              const oscillator = audioContextRef.current.createOscillator();
              const gainNode = audioContextRef.current.createGain();
              
              oscillator.connect(gainNode);
              gainNode.connect(audioContextRef.current.destination);
              
              oscillator.type = 'sine';
              oscillator.frequency.setValueAtTime(2000 + Math.random() * 1000, audioContextRef.current.currentTime);
              
              gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
              gainNode.gain.linearRampToValueAtTime(0.04, audioContextRef.current.currentTime + 0.01);
              gainNode.gain.exponentialRampToValueAtTime(0.001, audioContextRef.current.currentTime + 0.3);
              
              oscillator.start();
              oscillator.stop(audioContextRef.current.currentTime + 0.3);
            }
          }, i * 100);
        }
      }, 1200);
      
    } catch (error) {
      console.warn('Erro ao reproduzir som de vitória:', error);
    }
  };

  const finishAnimation = () => {
    setIsSpinning(false);
    setPhase('finished');
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Mostrar valor final com efeito de "parada" nos números
    setTimeout(() => {
      setCurrentValue(newValue);
      const maxValue = Math.max(oldValue || 0, newValue || 0);
      const totalDigits = Math.max(3, maxValue.toString().length);
      const finalNumbers = newValue.toString().padStart(totalDigits, '0').split('');
      setDisplayNumbers(finalNumbers);
      
      // Tocar som de vitória
      setTimeout(() => {
        playWinSound();
      }, 100);
      
    }, 200);
    
    // Fechar modal após um tempo e limpar audio
    setTimeout(() => {
      cleanupAudio();
      onClose();
    }, 3000); // Dar mais tempo para apreciar a vitória
  };

  const cleanupAudio = () => {
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        // Aguardar um pouco antes de fechar para permitir que os sons terminem
        setTimeout(() => {
          if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close();
          }
        }, 1000);
      } catch (error) {
        // Ignorar erros ao fechar AudioContext
      }
    }
  };

  if (!isVisible) return null;

  // Calcular largura do modal baseado na quantidade de dígitos
  const digitCount = displayNumbers.length;
  let modalWidthClass = 'max-w-md';
  if (digitCount > 6) {
    modalWidthClass = 'max-w-lg';
  } else if (digitCount > 8) {
    modalWidthClass = 'max-w-xl';
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl p-6 sm:p-8 shadow-2xl border-4 ${modalWidthClass} w-full mx-2 ${
        type === 'points' 
          ? 'bg-gradient-to-br from-green-400 via-green-500 to-green-600 border-green-300' 
          : 'bg-gradient-to-br from-purple-400 via-purple-500 to-purple-600 border-purple-300'
      }`}>
        {/* Header com ícone */}
        <div className="text-center mb-6">
          <div className={`inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-4 ${
            type === 'points' ? 'animate-pulse' : 'animate-bounce'
          }`}>
            {type === 'points' ? (
              <Coins className="w-8 h-8 text-green-500" />
            ) : (
              <Trophy className="w-8 h-8 text-purple-500" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {type === 'points' ? 'Pontos Adicionados!' : 'Prêmio Resgatado!'}
          </h2>
          {type === 'reward' && rewardName && (
            <p className="text-yellow-100 text-lg font-semibold">{rewardName}</p>
          )}
        </div>

        {/* Máquina Caça-níquel */}
        <div className="bg-gray-900 rounded-xl p-6 mb-6 shadow-inner relative overflow-hidden">
          {/* Luzes piscando durante a animação */}
          {isSpinning && (
            <>
              <div className={`absolute top-2 left-2 w-3 h-3 rounded-full animate-ping ${
                type === 'points' ? 'bg-green-500' : 'bg-purple-500'
              }`}></div>
              <div className={`absolute top-2 right-2 w-3 h-3 rounded-full animate-ping ${
                type === 'points' ? 'bg-blue-500' : 'bg-pink-500'
              }`} style={{ animationDelay: '0.2s' }}></div>
              <div className={`absolute bottom-2 left-2 w-3 h-3 rounded-full animate-ping ${
                type === 'points' ? 'bg-yellow-500' : 'bg-indigo-500'
              }`} style={{ animationDelay: '0.4s' }}></div>
              <div className={`absolute bottom-2 right-2 w-3 h-3 rounded-full animate-ping ${
                type === 'points' ? 'bg-red-500' : 'bg-orange-500'
              }`} style={{ animationDelay: '0.6s' }}></div>
            </>
          )}
          
          {/* Display dos números rolando */}
          <div className={`flex justify-center items-center mb-4 relative ${
            digitCount <= 4 ? 'gap-2' : digitCount <= 6 ? 'gap-1' : 'gap-0.5'
          }`}>
            {/* Efeito de brilho quando termina */}
            {phase === 'finished' && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-pulse rounded-lg"></div>
            )}
            
            {displayNumbers.map((number, index) => {
              // Responsividade baseada na quantidade de dígitos
              const digitCount = displayNumbers.length;
              let cardClasses = 'bg-black text-green-400 font-mono font-bold flex items-center justify-center rounded border-2 border-green-500 shadow-lg relative transition-all duration-200 ';
              
              // Ajustar tamanho dos cards baseado na quantidade de dígitos
              if (digitCount <= 3) {
                cardClasses += 'text-4xl w-12 h-16 ';
              } else if (digitCount <= 4) {
                cardClasses += 'text-3xl w-10 h-14 ';
              } else if (digitCount <= 5) {
                cardClasses += 'text-2xl w-9 h-12 ';
              } else if (digitCount <= 6) {
                cardClasses += 'text-xl w-8 h-11 ';
              } else if (digitCount <= 8) {
                cardClasses += 'text-lg w-7 h-10 ';
              } else {
                cardClasses += 'text-base w-6 h-9 ';
              }
              
              // Adicionar animação específica por posição com fallback para muitos dígitos
              let animationClass = '';
              if (isSpinning) {
                if (index <= 8) {
                  animationClass = `slot-roll-${index}`;
                } else {
                  animationClass = 'slot-roll-many';
                }
              } else if (phase === 'finished') {
                animationClass = digitCount > 6 ? 'animate-pulse slot-multi-glow' : 'animate-bounce';
              }
              
              cardClasses += animationClass;
              
              return (
                <div
                  key={index}
                  className={cardClasses}
                  style={{
                    textShadow: '0 0 10px #22c55e',
                    boxShadow: phase === 'finished' 
                      ? (digitCount > 6 ? '0 0 15px #22c55e, 0 0 25px rgba(255, 215, 0, 0.3)' : '0 0 20px #22c55e')
                      : '0 0 10px rgba(34, 197, 94, 0.3)',
                    animationDelay: isSpinning ? `${index * 0.08}s` : '0s'
                  }}
                >
                  {number}
                  {/* Efeito de reflexo */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent rounded pointer-events-none"></div>
                  
                  {/* Efeito extra para muitos dígitos */}
                  {digitCount > 6 && phase === 'finished' && (
                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/10 to-transparent rounded pointer-events-none animate-pulse"></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pontuação atual */}
          <div className="text-center relative">
            <div className="text-white text-sm mb-1">Pontos Totais</div>
            <div className={`text-yellow-400 text-3xl font-bold transition-all duration-300 ${
              phase === 'finished' ? 'animate-pulse text-4xl' : ''
            }`}>
              {currentValue.toLocaleString()}
            </div>
            
            {/* Efeito de confete quando termina */}
            {phase === 'finished' && (
              <div className="absolute inset-0 pointer-events-none">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 bg-yellow-400 rounded animate-bounce"
                    style={{
                      left: `${20 + i * 15}%`,
                      animationDelay: `${i * 0.1}s`,
                      animationDuration: '0.8s'
                    }}
                  ></div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Efeitos visuais */}
        {phase === 'finished' && (
          <div className="text-center relative">
            {/* Explosão de estrelas */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              {[...Array(8)].map((_, i) => (
                <Star
                  key={i}
                  className="absolute w-4 h-4 text-yellow-400 animate-ping"
                  style={{
                    left: `${50 + Math.cos(i * Math.PI / 4) * 60}%`,
                    top: `${50 + Math.sin(i * Math.PI / 4) * 60}%`,
                    animationDelay: `${i * 0.1}s`,
                    animationDuration: '1s'
                  }}
                />
              ))}
            </div>
            
            <div className={`flex justify-center items-center gap-2 ${
              type === 'points' ? 'text-green-200' : 'text-purple-200'
            } ${phase === 'finished' ? 'animate-bounce' : ''}`}>
              <Star className="w-6 h-6 animate-spin" />
              <span className="font-bold text-lg slot-rainbow">
                {type === 'points' 
                  ? `+${(newValue - oldValue).toLocaleString()} pontos!`
                  : `${oldValue - newValue} pontos utilizados!`
                }
              </span>
              <Star className="w-6 h-6 animate-spin" style={{ animationDirection: 'reverse' }} />
            </div>
            
            {/* Efeito de chuva de moedas */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-3 h-3 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full animate-bounce opacity-80"
                  style={{
                    left: `${10 + i * 7}%`,
                    top: '-10px',
                    animationDelay: `${i * 0.15}s`,
                    animationDuration: '1.5s',
                    animationIterationCount: '3'
                  }}
                >
                  <div className="w-full h-full rounded-full bg-gradient-to-t from-yellow-300 to-yellow-100 opacity-60"></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Indicador de loading durante spin */}
        {isSpinning && (
          <div className="text-center">
            <div className="text-yellow-100 animate-pulse">
              Processando...
            </div>
          </div>
        )}
      </div>


    </div>
  );
};

export default SlotMachineAnimation;
